import type {
  TrackerSourcePostingFormatSummary,
  TrackerSourcePostingReference,
} from '@shared/tracker-types';

export type ReferenceReviewTaskType = 'REMOVAL' | 'REPOST' | 'POSTING';
export type ReferenceKind = NonNullable<TrackerSourcePostingReference['referenceKind']>;

export function getReferenceHistoryStepCount(references: TrackerSourcePostingReference[]): number {
  return groupReferencesByKind(references).length + 1;
}

export function getReferenceKindGroupCount(references: TrackerSourcePostingReference[]): number {
  return groupReferencesByKind(references).length;
}

export function getReferencePanelCopyForKind(
  referenceKind: ReferenceKind,
  step?: number,
  totalSteps?: number,
) {
  const stepPrefix = step && totalSteps && totalSteps > 2 ? `Step ${step} of ${totalSteps} · ` : '';

  switch (referenceKind) {
    case 'removal_source':
      return {
        badgeLabel: 'Original posting',
        title: `${stepPrefix}Prior-cycle install`,
        subtitle: 'For comparison only',
        description: 'What was installed in the earlier cycle — not what you approve or reject now.',
        notUnderReviewLabel: 'Not under review',
        imageLabel: 'Original posting',
      };
    case 'repost_rejected_removal':
      return {
        badgeLabel: 'Rejected removal',
        title: `${stepPrefix}Rejected removal attempt`,
        subtitle: 'For comparison only',
        description: 'The removal submission that was rejected before this repost was sent out.',
        notUnderReviewLabel: 'Not under review',
        imageLabel: 'Rejected removal',
      };
    case 'repost_rejected':
    default:
      return {
        badgeLabel: 'Rejected submission',
        title: `${stepPrefix}Previously rejected posting`,
        subtitle: 'For comparison only',
        description: 'The submission that was rejected before this repost was sent out.',
        notUnderReviewLabel: 'Not under review',
        imageLabel: 'Rejected submission',
      };
  }
}

export function getCurrentEvidencePanelCopy(
  taskType?: ReferenceReviewTaskType | string | null,
  referenceStepCount = 0,
) {
  const normalized = (taskType || '').toUpperCase();
  const isThreeStepRemovalRepost = normalized === 'REPOST' && referenceStepCount >= 2;
  const stepLabel = isThreeStepRemovalRepost ? 'Step 3 of 3 · ' : referenceStepCount === 1 ? 'Step 2 of 2 · ' : '';

  if (normalized === 'REPOST') {
    return {
      title: `${stepLabel}Current repost evidence`,
      subtitle: 'Approve or reject this',
      description: isThreeStepRemovalRepost
        ? 'The new removal repost you are reviewing now — not the rejected removal or original posting.'
        : 'The new submission you are reviewing now — not the rejected attempt shown for comparison.',
    };
  }

  if (normalized === 'REMOVAL') {
    return {
      title: `${stepLabel}Current removal evidence`,
      subtitle: 'Approve or reject this',
      description: referenceStepCount >= 1
        ? 'The removal submission you are reviewing now — not the original posting.'
        : 'The removal submission you are reviewing now.',
    };
  }

  return {
    title: `${stepLabel}Current submission evidence`,
    subtitle: 'Approve or reject this',
    description: 'This is the evidence you are reviewing now.',
  };
}

export function getReviewDecisionScopeHint(
  taskType?: ReferenceReviewTaskType | string | null,
  references: TrackerSourcePostingReference[] = [],
) {
  const normalized = (taskType || '').toUpperCase();
  const kinds = new Set(references.filter((ref) => ref.imageUrl).map((ref) => ref.referenceKind));

  if (normalized === 'REPOST' && kinds.has('removal_source') && kinds.has('repost_rejected_removal')) {
    return 'Approve or reject applies to the current repost evidence only — not the rejected removal or original posting.';
  }

  if (normalized === 'REPOST') {
    return 'Approve or reject applies to the current repost evidence only — not the rejected submission.';
  }

  if (normalized === 'REMOVAL') {
    return 'Approve or reject applies to the current removal evidence only — not the original posting.';
  }

  return 'Approve or reject applies to the current submission only.';
}

export function getOriginalImageAltLabel(
  referenceKind: ReferenceKind | undefined,
  formatName?: string | null,
): string {
  const suffix = formatName ? ` — ${formatName}` : '';
  const copy = getReferencePanelCopyForKind(referenceKind ?? 'repost_rejected');
  return `${copy.imageLabel}${suffix}`;
}

export function formatReferenceFormatSummaries(
  summaries: TrackerSourcePostingFormatSummary[] | undefined,
  fallback?: Pick<TrackerSourcePostingReference, 'formatId' | 'formatName'>,
): string {
  if (summaries && summaries.length > 0) {
    return summaries
      .map((summary) => {
        const name = summary.formatName?.trim() || `Format ${summary.formatId}`;
        const quantity = Math.max(0, Number(summary.quantity) || 0);
        return `${name} × ${quantity}`;
      })
      .join(' · ');
  }

  if (fallback?.formatName?.trim()) {
    return fallback.formatName.trim();
  }

  if (fallback?.formatId) {
    return `Format ${fallback.formatId}`;
  }

  return 'Formats not recorded';
}

export function getReferenceMetaLabel(
  ref: TrackerSourcePostingReference,
  options?: { compact?: boolean },
): string {
  const compact = options?.compact ?? false;

  switch (ref.referenceKind) {
    case 'removal_source':
      if (ref.sourceType === 'confirmed_delivery') {
        return compact ? 'Approved install' : 'Original posting · approved install';
      }
      if (ref.sourceType === 'planned_delivery') {
        return compact ? 'Planned record' : 'Original posting · planned record';
      }
      return compact ? 'Reference photo' : 'Original posting photo';
    case 'repost_rejected_removal':
      return compact ? 'Rejected attempt' : 'Rejected removal attempt';
    case 'repost_rejected':
      return compact ? 'Rejected attempt' : 'Rejected submission';
    default:
      return 'Reference photo';
  }
}

export function getReferenceTimestampLabel(referenceKind: ReferenceKind | undefined): string {
  return referenceKind === 'removal_source' ? 'Approved' : 'Rejected';
}

export function getReferenceOperatorVerb(referenceKind: ReferenceKind | undefined): string {
  return referenceKind === 'removal_source' ? 'Posted' : 'Submitted';
}

/** Chronological order for reference panels left-to-right / top-to-bottom. */
export const REFERENCE_KIND_ORDER: ReferenceKind[] = [
  'removal_source',
  'repost_rejected_removal',
  'repost_rejected',
];

export function groupReferencesByKind(
  references: TrackerSourcePostingReference[],
): Array<{ kind: ReferenceKind; references: TrackerSourcePostingReference[] }> {
  const withImages = references.filter((ref) => ref.imageUrl);
  const grouped = new Map<ReferenceKind, TrackerSourcePostingReference[]>();

  for (const ref of withImages) {
    const kind = ref.referenceKind ?? 'repost_rejected';
    const bucket = grouped.get(kind) ?? [];
    bucket.push(ref);
    grouped.set(kind, bucket);
  }

  return REFERENCE_KIND_ORDER
    .filter((kind) => grouped.has(kind))
    .map((kind) => ({ kind, references: grouped.get(kind)! }));
}

export function getTaskTypeActionLabel(taskType?: ReferenceReviewTaskType | string | null): string {
  const normalized = (taskType || '').toUpperCase();
  if (normalized === 'REMOVAL') return 'Removed';
  if (normalized === 'REPOST') return 'Reposted';
  return 'Installed';
}
